import { useState } from 'react'
import { Shield } from 'lucide-react'
import { useUsersApi } from 'src/api/db/apiUsers'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Avatar, AvatarImage } from 'src/shared/components/ui/avatar'
import { Badge } from 'src/shared/components/ui/badge'
import { cn } from 'src/shared/components/ui/utils'
import { capitalize } from 'src/shared/utils'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'

export const SidebarAccountButton = ({
  onClick,
  isCollapsed = false,
}: {
  onClick: () => void
  isCollapsed?: boolean
}) => {
  const { user } = useUsersApi()
  const { organization } = useOrgsApi({})
  const [logoError, setLogoError] = useState(false)
  const isAdmin = useIsAdmin()
  const { t } = useLocalization()

  const firstName = user?.data?.first_name
    ? capitalize(user.data.first_name)
    : null
  const lastName = user?.data?.last_name
    ? capitalize(user.data.last_name)
    : null
  const userName =
    [firstName, lastName].filter(Boolean).join(' ') ||
    user?.data?.email?.split('@')[0] ||
    'Guest'

  const companyName = organization?.data?.name || null
  const companyDisplayName =
    companyName && companyName !== 'Anon'
      ? companyName
      : t('Not signed in', 'Ej inloggad')
  const companyLogoUrl = organization?.data?.chosen_logo_url || null
  const showLogo = companyLogoUrl && !logoError

  return (
    <Row
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      title={isCollapsed ? userName : undefined}
      className={cn(
        'sidebar-account-btn w-full cursor-pointer items-center',
        isCollapsed ? 'justify-center px-0 py-2' : 'gap-3 px-3 py-2.5'
      )}
    >
      <Avatar size="md" className="h-8 w-8 shrink-0 rounded-md">
        {showLogo && (
          <AvatarImage
            src={companyLogoUrl}
            alt={companyName || 'Company logo'}
            className="h-full w-full object-contain p-1"
            onError={() => setLogoError(true)}
          />
        )}
        <div className="flex h-full w-full items-center justify-center rounded-md bg-muted text-xs font-medium">
          {userName[0]?.toUpperCase() || 'G'}
        </div>
      </Avatar>

      {!isCollapsed && (
        <Column className="min-w-0 flex-1 gap-0.5">
          <Row className="min-w-0 items-center gap-1.5">
            <span className="truncate text-sm font-semibold leading-tight text-neutral-900">
              {userName}
            </span>
            {isAdmin && (
              <Badge
                variant="orange"
                className="flex shrink-0 items-center gap-1 px-1.5 py-0"
              >
                <Shield className="h-3 w-3" />
              </Badge>
            )}
          </Row>
          <span className="truncate text-xs leading-tight text-neutral-500">
            {companyDisplayName}
          </span>
        </Column>
      )}
    </Row>
  )
}
